import { FieldArray, useField } from "formik";
import React, { useState, useEffect, useRef } from "react";
import BaseElement from "components/FormElements/BaseElement";
import { CheckIcon } from "@heroicons/react/solid";
import { useAuth } from "hooks/Auth";
import { apiGet } from "helpers/Request";
import { transform as channelTransformer } from "schemas/Channel";
import { transform as entryTransformer } from "schemas/Entry";
import SelectBox from "components/FormElements/SelectBox";
import { packClasses } from "Utils";

export default function RelatedentriesMetaProperty(props) {
    const auth = useAuth();
    const [field, meta] = useField(props);
    const [entries, setEntries] = useState([]);
    const { channel } = props.metaProps;

    const hasIndex = (entry) => {
        if (!field.value) {
            return -1;
        }
        return field.value.findIndex((e) => e === entry.uuid);
    };

    useEffect(() => {
        apiGet("/v4/entry", auth.publication, { channel: channel }).then(
            (res) => {
                const entries = res.data.map(entryTransformer);
                setEntries(entries);
            }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channel, auth.publication]);

    const input = (
        <div>
            <FieldArray
                name={field.name}
                render={(arrayHelpers) => (
                    <div className="focus:outline-none relative max-h-48 w-full cursor-default overflow-auto rounded-md border border-gray-300 bg-white text-left shadow-sm focus:border-green-500 focus:ring-1 focus:ring-green-500 disabled:opacity-50 sm:text-sm">
                        {entries.map((entry, index) => (
                            <div key={index}>
                                <div
                                    className={packClasses(
                                        "relative cursor-default select-none py-2 pl-8 pr-4",
                                        hasIndex(entry) !== -1
                                            ? "bg-green-600 text-white"
                                            : "text-gray-900"
                                    )}
                                >
                                    <button
                                        type="button"
                                        className={packClasses(
                                            "block w-full truncate text-left",
                                            hasIndex(entry) !== -1
                                                ? "font-semibold"
                                                : "font-normal"
                                        )}
                                        onClick={() => {
                                            const selected = hasIndex(entry);
                                            if (selected !== -1) {
                                                arrayHelpers.remove(selected);
                                            } else {
                                                arrayHelpers.push(entry.uuid);
                                            }
                                        }}
                                    >
                                        {entry.title}
                                    </button>
                                    {hasIndex(entry) !== -1 && (
                                        <span
                                            className={packClasses(
                                                "text-white",
                                                "absolute inset-y-0 left-0 flex items-center pl-1.5"
                                            )}
                                        >
                                            <CheckIcon
                                                className="h-5 w-5"
                                                aria-hidden="true"
                                            />
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            />
        </div>
    );

    const label = (
        <label
            htmlFor={props.id}
            className="block text-sm font-medium text-gray-700"
        >
            {props.label}
        </label>
    );

    const helperText = props.helpertext ? (
        <p className="mt-2 text-sm text-gray-500">{props.helpertext}</p>
    ) : (
        ""
    );

    const errorText =
        props.isError || (meta.touched && meta.error) ? (
            <p className="mt-2 text-sm text-red-600">
                {props.errorText}
                {meta.error}
            </p>
        ) : (
            ""
        );

    return (
        <BaseElement
            {...props}
            input={input}
            label={label}
            helpertext={helperText}
            errortext={errorText}
        />
    );
}

export const Property = RelatedentriesMetaProperty;

export const Label = "Select Entries";

export function Configure(props) {
    const [channels, setChannels] = useState([]);

    const auth = useAuth();
    const ref = useRef(null);

    useEffect(() => {
        if (ref.current) {
            return;
        }

        apiGet("/v4/channel", auth.publication).then((res) => {
            setChannels(channelTransformer(res));
            ref.current = true;
        });

        return () => {
            setChannels([]);
            ref.current = null;
        };
    }, [auth.publication]);

    return (
        <div>
            <SelectBox
                name={`${props.fieldName}.channel`}
                options={channels.map((ch) => ({
                    label: ch.title,
                    value: ch.id,
                }))}
            />
        </div>
    );
}
